// src/components/sections/SimpleImageGallery.jsx
import React, { useState, useEffect } from 'react';
/**
 * A simple gallery component that displays images from a Google Drive folder
 * using Google Drive's direct embed approach - no API key required.
 * 
 * @param {Object} props
 * @param {string} props.title - Gallery title
 * @param {string} props.folderId - Google Drive folder ID
 */
const SimpleImageGallery = ({ title = "Photo Gallery", folderId }) => {
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [iframeHeight, setIframeHeight] = useState('600px');
  
  // Handle window resize for responsive iframe height
  useEffect(() => {
    const updateHeight = () => {
      // Adjust iframe height based on screen size
      if (window.innerWidth < 768) {
        setIframeHeight('400px');
      } else {
        setIframeHeight('600px');
      }
    };
    
    window.addEventListener('resize', updateHeight);
    updateHeight(); // Set initial height
    
    return () => window.removeEventListener('resize', updateHeight);
  }, []);
  
  // Check if the folderId is provided
  useEffect(() => {
    if (!folderId) {
      setError('No Google Drive folder ID provided');
    } else {
      setImagesLoaded(true);
    }
  }, [folderId]);
  
  if (error) {
    return (
      <section id="gallery" className="min-h-screen py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold mb-8 text-center">{title}</h2>
          <div className="text-center text-red-500">{error}</div>
        </div>
      </section>
    );
  }
  
  return (
    <section id="gallery" className="min-h-screen py-16 px-4 bg-gray-50">
      <div className="max-w-6xl mx-auto">
        <h2 className="text-4xl font-bold mb-8 text-center">{title}</h2>
        {/* Additional Information */}
        <div className="mt-12 text-center">
          <h3 className="text-2xl font-bold mb-4">About This Gallery</h3>
          <p className="text-gray-700 max-w-3xl mx-auto">
            Photos from Adelaide Samba Encontro weekend. All photos taken by Alex Kwong. For use in social media, please include credit to <b>@alexkwongphotography</b>
          </p><br></br>
        </div>
        <div className="w-full bg-white rounded-lg shadow-md overflow-hidden">
          {/* Instructions Banner */}
          <div className="bg-[#2E0854] text-white p-4 text-center">
            <p>Click on any photo to view</p>
          </div>
          
          {/* Google Drive Embedded Folder */}
          <div className="w-full">
            <iframe 
              src={`https://drive.google.com/embeddedfolderview?id=${folderId}#grid`}
              width="100%" 
              height={iframeHeight} 
              frameBorder="0"
              title="Google Drive Photo Gallery"
              className="w-full"
              onLoad={() => setImagesLoaded(true)}
              onError={() => setError('Failed to load gallery from Google Drive')}
            />
          </div>
          
          {/* Loading State */}
          {!imagesLoaded && (
            <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#2E0854]"></div>
            </div>
          )}
          
          {/* View Full Gallery Link */}
          <div className="bg-gray-100 p-4 text-center">
            <a 
              href={`https://drive.google.com/drive/folders/${folderId}?usp=sharing`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#2E0854] hover:text-[#FF4500] font-medium"
            >
              Open Full Gallery in Google Drive
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SimpleImageGallery;